import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Landmark, Copy, CheckCircle2 } from "lucide-react";
import { eur } from "@/lib/format";

export default function BankTransferPayment({ order, onRequested }) {
  const [cfg, setCfg] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get("/config/bank-transfer").then((r) => setCfg(r.data)).catch(() => setCfg({})); }, []);

  const requestTransfer = async () => {
    setSubmitting(true);
    try {
      await api.post(`/orders/${order.id}/bank-transfer/request`);
      toast.success("Richiesta bonifico registrata. Riceverai email con le istruzioni.");
      onRequested?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore richiesta bonifico");
    } finally { setSubmitting(false); }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard?.writeText(text);
    toast.success(`${label} copiato`);
  };

  if (!cfg) return <div className="text-slate-500 text-sm">Caricamento…</div>;

  const causale = `${order.order_number}`;
  const alreadyRequested = order.payment_method === "bonifico" && order.payment_status === "in_attesa";

  return (
    <div className="border border-slate-200 rounded-xl p-4 bg-slate-50/40" data-testid="bank-transfer-section">
      <div className="flex items-center gap-2 mb-3">
        <Landmark className="w-4 h-4 text-[#0F2942]" />
        <div className="font-display font-bold text-slate-900">Bonifico bancario</div>
      </div>
      <div className="space-y-2 text-sm">
        <Row label="Intestatario" value={cfg.holder || "-"} onCopy={() => copyToClipboard(cfg.holder, "Intestatario")} />
        <Row label="IBAN" mono value={cfg.iban || "-"} onCopy={() => copyToClipboard(cfg.iban, "IBAN")} testid="bank-iban" />
        {cfg.bank_name && <Row label="Banca" value={cfg.bank_name} />}
        {cfg.bic && <Row label="BIC/SWIFT" mono value={cfg.bic} onCopy={() => copyToClipboard(cfg.bic, "BIC")} />}
        <Row label="Causale" mono value={causale} onCopy={() => copyToClipboard(causale, "Causale")} testid="bank-causale" />
        <Row label="Importo" value={eur(order.total)} />
      </div>
      {alreadyRequested ? (
        <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm flex items-start gap-2">
          <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
          <span>Bonifico richiesto · in attesa di conferma dall'amministrazione.</span>
        </div>
      ) : (
        <Button onClick={requestTransfer} disabled={submitting}
          className="w-full mt-4 h-11 bg-[#0F2942] hover:bg-[#1B3B5F] text-white"
          data-testid="bank-transfer-confirm-button">
          <Landmark className="w-4 h-4 mr-1" /> {submitting ? "Invio richiesta…" : "Ho eseguito il bonifico"}
        </Button>
      )}
      <div className="text-xs text-slate-500 mt-2">
        L'ordine resterà <strong>in verifica</strong> finché Centro Studi Athena non conferma l'incasso.
      </div>
    </div>
  );
}

function Row({ label, value, mono, onCopy, testid }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">{label}</div>
      <div className="flex items-center gap-2">
        <div className={`text-sm text-slate-900 ${mono ? "font-mono" : ""}`} data-testid={testid}>{value}</div>
        {onCopy && (
          <button onClick={onCopy} className="p-1 text-slate-400 hover:text-slate-700" title={`Copia ${label}`}>
            <Copy className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
